import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Text } from '@components/rolex-ui/Text'
import { cn } from '@lib/cn'
import { Container } from '@components/gassan-ui'
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock'
import { Icon } from '@components/rolex-ui/Icon'

type NavItem = {
  label: string
  href: string
  variant?: 'rolex' | 'rcpo'
}

const MobileNav: React.FC<{
  navItems: NavItem[]
  language: string
  routerPath: string
  variant?: 'rolex' | 'rcpo'
}> = ({ navItems, language, routerPath, variant = 'rolex' }) => {
  const ref = useRef()
  const [isMobileActive, setIsMobileActive] = useState(false)
  const disableMobileMenu = () => setIsMobileActive(false)
  const toggleMobileMenu = () => setIsMobileActive(!isMobileActive)

  useEffect(() => {
    if (isMobileActive) {
      ref?.current && disableBodyScroll(ref.current)
    } else {
      ref?.current && enableBodyScroll(ref.current)
    }
  }, [isMobileActive])

  useEffect(() => {
    disableMobileMenu()
  }, [routerPath])

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="rolex-mobile-nav items-center rounded border-none text-white outline-none focus:ring-2 focus:ring-rolexOceanGreen"
        aria-label={isMobileActive ? 'Close menu' : 'Open menu'}
        aria-expanded={isMobileActive}
      >
        <Text variant="fixed16" className="-mr-2">
          Menu
        </Text>
        <div className={cn('transition-transform duration-100', isMobileActive && 'rotate-180')}>
          <Icon icon="rolex-mobile-menu-arrow" className="h-12 w-12" />
        </div>
      </button>
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-20 h-[calc(100dvh-2rem)] w-full -translate-y-2 py-10 opacity-0 transition-all duration-150 lg:hidden',
          variant === 'rcpo'
            ? 'bg-black'
            : 'bg-transparent bg-gradient-to-r from-rolexGreenDark to-rolexPrimary',
          isMobileActive && 'pointer-events-auto translate-y-0 opacity-100',
        )}
      >
        <Container
          as="nav"
          className="flex flex-col gap-9"
          ref={ref}
          aria-label="Mobile navigation"
        >
          {navItems.map((item, key) => {
            const cleanHref = item.href.replace(`/${language}`, '')
            const isActive = cleanHref === routerPath

            return (
              <Link
                href={item.href}
                key={key}
                type="link"
                className="rounded outline-none focus:outline-none focus:ring-2 focus:ring-rolexOceanGreen"
                aria-current={isActive ? 'page' : undefined}
              >
                <Text
                  variant={variant === 'rcpo' ? 'fixed22' : 'fixed16'}
                  className={cn(
                    'text-white transition-colors duration-100',
                    isActive && 'text-rolexOceanGreen',
                  )}
                >
                  {item.label}
                </Text>
              </Link>
            )
          })}
        </Container>
      </div>
    </>
  )
}

export default MobileNav
